import React, { useState, ReactNode, MouseEvent } from 'react';

type Side = 'left' | 'right';

interface HorizontalResizableProps {
  minWidth?: number;
  maxWidth?: number;
  initialWidth?: number;
  side?: Side;
  children: ReactNode;
  onResize?: (width: number) => void;
  onResizeStop?: () => void;
}

const HorizontalResizable: React.FC<HorizontalResizableProps> = ({
  minWidth = 300,
  maxWidth = 1000,
  initialWidth = 300,
  side = 'right',
  children,
  onResize = () => {},
  onResizeStop = () => {},
}) => {
  const [width, setWidth] = useState<number>(initialWidth);

  const onMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = width;

    const onMouseMove = (m: MouseEvent | MouseEvent) => {
      const event = m as MouseEvent & { clientX: number };
      const delta = event.clientX - startX;

      let newWidth = side === 'right' ? startWidth + delta : startWidth - delta;

      newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
      setWidth(newWidth);
      onResize(newWidth);
    };

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove as any);
      window.removeEventListener('mouseup', onMouseUp);
      onResizeStop();
    };

    window.addEventListener('mousemove', onMouseMove as any);
    window.addEventListener('mouseup', onMouseUp);
  };

  return (
    <div className="resizable" style={{ width }}>
      {children}
      <div
        className={`resize-handle ${side}`}
        onMouseDown={onMouseDown}
        role="slider"
        aria-label={`Resize handle on the ${side} side`}
        aria-valuemin={minWidth}
        aria-valuemax={maxWidth}
        aria-valuenow={width}
        tabIndex={0}
      />
    </div>
  );
};

export default HorizontalResizable;
